import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useLocalStorageState } from './useLocalStorageState';

interface SidebarContextType {
    isFixed: boolean;
    isHovered: boolean;
    toggle: () => void;
    show: () => void;
    hide: () => void;
    setHovered: (hovered: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextType | null>(null);

const isMacSidebarShortcutPlatform = () =>
    typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

export const getSidebarShortcutLabel = () => (isMacSidebarShortcutPlatform() ? '⌘ + B' : 'Ctrl + B');

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

export const useSidebarState = () => {
    const [isFixedRaw, setIsFixedRaw] = useLocalStorageState('closelink-sidebar-fixed', true);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const isFixed = Boolean(isFixedRaw);
    const setIsFixed = useCallback(
        (value: boolean | ((prev: boolean) => boolean)) => {
            if (typeof value === 'function') {
                setIsFixedRaw((prev) => value(Boolean(prev)));
            } else {
                setIsFixedRaw(value);
            }
        },
        [setIsFixedRaw]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Cmd+B (macOS) or Ctrl+B (Windows/Linux)
            if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
                // Prevent default browser behavior (like opening bookmarks)
                event.preventDefault();
                event.stopPropagation();
                setIsFixed((prev) => !prev);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [setIsFixed]);

    const toggle = () => setIsFixed((prev) => !prev);
    const show = () => setIsFixed(true);
    const hide = () => setIsFixed(false);

    const setHovered = (hovered: boolean) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        if (hovered) {
            setIsHovered(true);
        } else {
            // Add delay before hiding
            hoverTimeoutRef.current = setTimeout(() => {
                setIsHovered(false);
            }, 200);
        }
    };

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    return {
        isFixed,
        isHovered,
        toggle,
        show,
        hide,
        setHovered,
    };
};
